import React from 'react';
import { useAgencyBranding } from '../../branding';
import PropTypes from 'prop-types';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface AccordionItem {
	title: string;
	content: React.ReactNode;
}

const DEFAULT_ACCENT_COLOR = '#31194B';

interface CustomAccordionProps {
	title: string;
	subtitle?: string; // Opcional
	items: AccordionItem[];
	accentColor?: string;
}

const CustomAccordion: React.FC<CustomAccordionProps> = ({
	title,
	subtitle,
	items,
	accentColor = DEFAULT_ACCENT_COLOR,
}) => {
	const branding = useAgencyBranding();
	return (
		<div>
			<Container maxWidth="md" sx={{ paddingTop: '16px' }}>
				<Typography
					component="h1"
					variant="h6"
					sx={{
						marginBottom: '10px',
						width: '100%',
						background: accentColor === DEFAULT_ACCENT_COLOR ? branding.headerGradient : accentColor,
						paddingLeft: '18px',
						paddingRight: '18px',
						paddingTop: '10px',
						paddingBottom: '10px',
						borderRadius: '10px',
						fontSize: '1rem',
						fontWeight: 700,
						color: 'white',
						boxShadow: '0 8px 20px rgba(49, 25, 75, 0.16)',
					}}
				>
					{title}
				</Typography>
				{subtitle && (
					<Typography
						variant="body2"
						sx={{
							px: 1,
							pb: 1,
							color: 'text.secondary',
							fontWeight: 500,
							borderLeft: '3px solid',
							borderColor: 'secondary.main',
							ml: 0.5,
							mb: 1,
						}}
					>
						{subtitle}
					</Typography>
				)}
				<Box
					sx={{
						borderRadius: '10px',
						border: '1px solid rgba(49, 25, 75, 0.10)',
						boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
						overflow: 'hidden',
						backgroundColor: '#fff',
					}}
				>
					{items.map((item, index) => (
						<Accordion
							key={index}
							disableGutters
							elevation={0}
							sx={{
								'&:before': { display: 'none' },
								borderBottom: index + 1 === items.length ? 'none' : '1px solid rgba(49, 25, 75, 0.07)',
							}}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon color="primary" />}
								sx={{
									fontFamily: 'Montserrat, sans-serif',
									display: 'flex',
									alignItems: 'center',
									minHeight: 44,
									'& .MuiAccordionSummary-content': { my: 1 },
								}}
							>
								<Typography variant="body2" color="primary" fontWeight={800}>
									{item.title}
								</Typography>
							</AccordionSummary>
							<AccordionDetails
								sx={{
									pt: 0,
									pb: 1.5,
									color: 'text.secondary',
									'& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.45 },
								}}
							>
								{item.content}
							</AccordionDetails>
						</Accordion>
					))}
				</Box>
			</Container>
		</div>
	);
};

// PropTypes con validación
CustomAccordion.propTypes = {
	title: PropTypes.string.isRequired,
	subtitle: PropTypes.string,
	items: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			content: PropTypes.node.isRequired,
		}).isRequired,
	).isRequired,
	accentColor: PropTypes.string,
};

export default CustomAccordion;
